import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "../lib/trpc.js";
import { Card, Empty, ErrorBox, Spinner, StatTile } from "../components/ui.js";
import GameRow from "../components/GameRow.js";
import StandingsTable from "../components/StandingsTable.js";
import { formatDate } from "../lib/format.js";
import { useSession } from "../lib/session.js";

/** The small cup on the champion line, matching the trophy case on a profile. */
function TrophyGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-3.5 w-3.5"
      aria-hidden
    >
      <path d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
      <path d="M7 6H4.5v1.5A3.5 3.5 0 0 0 8 11" />
      <path d="M17 6h2.5v1.5A3.5 3.5 0 0 1 16 11" />
      <path d="M12 14v3" />
      <path d="M9.5 20h5l-.5-3h-4l-.5 3Z" />
    </svg>
  );
}

export default function Home() {
  const trpc = useTRPC();
  const { user } = useSession();

  const standings = useQuery(trpc.league.standings.queryOptions({}));
  const recent = useQuery(trpc.league.recent.queryOptions({ limit: 6 }));
  const news = useQuery(trpc.articles.list.queryOptions({ limit: 4 }));
  const honours = useQuery(trpc.league.honours.queryOptions());
  const queue = useQuery({
    ...trpc.games.myQueue.queryOptions(),
    enabled: Boolean(user),
  });

  const season = standings.data?.season;

  // Most recent finished games and the next unplayed ones, across every stage,
  // so playoff and tournament results show up here too.
  const recentGames = recent.data?.results ?? [];
  const upcomingGames = recent.data?.upcoming ?? [];
  // Results only fall back to an earlier competition when this season has none,
  // so the card has to say which it is showing.
  const resultsAreOld = recent.data?.resultsFromThisSeason === false;

  // The reigning champion — the last title awarded in league history. The
  // honours list runs oldest competition first.
  const champion = [...(honours.data ?? [])]
    .reverse()
    .find((h) => h.kind === "champion");

  const todo =
    (queue.data?.awaitingMyConfirmation.length ?? 0) +
    (queue.data?.unplayed.length ?? 0);

  return (
    <div className="space-y-8">
      <section className="rounded-2xl border border-field-700/60 bg-gradient-to-br from-field-900 to-field-950 px-6 py-8">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blitz-400">
          {season?.name ?? "Madden Pro League"}
        </p>
        <h1 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">
          The Madden Pro League
        </h1>
        <p className="mt-3 max-w-2xl text-chalk-400">
          Ten managers, two divisions, fourteen games each. Every result, every
          stat line and every article the league has produced, in one place.
        </p>

        {season && (
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatTile
              label="Season"
              value={season.name}
              hint={season.rulesRevision ? `Rules Rev ${season.rulesRevision}` : undefined}
            />
            <StatTile
              label="Format"
              value={`${season.regularSeasonWeeks ?? "—"} weeks`}
              hint={`${season.quarterMinutesRegular}-min quarters`}
            />
            <StatTile
              label="Playoffs"
              value={
                season.playoffBracketSize ? `${season.playoffBracketSize} teams` : "—"
              }
              hint="Double elimination"
            />
            <StatTile
              label="Managers"
              value={standings.data?.rows.length ?? "—"}
              hint={season.status.replace("_", " ")}
            />
          </div>
        )}

        {champion && (
          <p className="mt-6 flex flex-wrap items-center gap-2 text-sm text-chalk-400">
            <span className="chip border-flag-500/50 bg-flag-500/10 text-flag-400">
              <TrophyGlyph />
              {champion.competition} champion
            </span>
            {champion.managerSlug ? (
              <Link
                to={`/managers/${champion.managerSlug}`}
                className="font-semibold text-chalk-100 hover:text-blitz-400"
              >
                {champion.managerName}
              </Link>
            ) : (
              <span className="font-semibold text-chalk-100">
                {champion.managerName}
              </span>
            )}
          </p>
        )}

        {user && todo > 0 && (
          <Link
            to="/submit"
            className="mt-6 inline-flex items-center gap-2 rounded-lg border border-flag-500/40 bg-flag-500/10 px-4 py-2 text-sm font-semibold text-flag-400 hover:bg-flag-500/20"
          >
            You have {todo} game{todo === 1 ? "" : "s"} needing attention →
          </Link>
        )}
      </section>

      <div className="grid min-w-0 gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          {upcomingGames.length > 0 && (
            <Card
              title="Up next"
              action={
                // The league's next games, with a way through to just yours.
                user ? (
                  <Link
                    to={`/season?view=schedule&manager=${user.slug}`}
                    className="link text-xs"
                  >
                    Your schedule
                  </Link>
                ) : null
              }
            >
              {upcomingGames.map((g) => (
                <GameRow key={g.id} game={g} />
              ))}
            </Card>
          )}

          {recentGames.length > 0 && (
            <Card
              title={resultsAreOld ? "Last time out" : "Latest results"}
              action={
                <Link
                  to={
                    resultsAreOld && recent.data?.resultsSeason
                      ? `/season?season=${recent.data.resultsSeason.slug}&view=schedule`
                      : "/season?view=schedule"
                  }
                  className="link text-xs"
                >
                  {resultsAreOld && recent.data?.resultsSeason
                    ? recent.data.resultsSeason.name
                    : "Full schedule"}
                </Link>
              }
            >
              {recentGames.map((g) => (
                <GameRow key={g.id} game={g} />
              ))}
              {resultsAreOld && (
                <p className="border-t border-field-800 px-4 py-3 text-xs text-chalk-500">
                  {season?.name ?? "This season"} hasn&apos;t kicked off yet — these
                  are the last games the league played
                  {recent.data?.resultsSeason
                    ? `, in ${recent.data.resultsSeason.name}`
                    : ""}
                  .
                </p>
              )}
            </Card>
          )}

          {recent.isLoading && <Spinner />}
          {recent.error && <ErrorBox error={recent.error} />}
        </div>

        <div className="min-w-0 space-y-6">
          <Card
            title="Standings"
            action={
              <Link to="/season" className="link text-xs">
                Full table
              </Link>
            }
          >
            {standings.isLoading && <Spinner />}
            {standings.error && (
              <div className="p-4">
                <ErrorBox error={standings.error} />
              </div>
            )}
            {standings.data &&
              (standings.data.rows.length === 0 ? (
                <Empty>No games played yet this season.</Empty>
              ) : (
                <StandingsTable
                  rows={standings.data.rows}
                  seeds={standings.data.seeds}
                  compact
                />
              ))}
          </Card>

          <Card
            title="Latest news"
            action={
              <Link to="/news" className="link text-xs">
                All articles
              </Link>
            }
          >
            {news.isLoading && <Spinner />}
            {news.data?.articles.length === 0 && <Empty>No articles yet.</Empty>}
            <ul>
              {news.data?.articles.map((a) => (
                <li
                  key={a.id}
                  className="border-b border-field-800 last:border-b-0"
                >
                  <Link
                    to={`/news/${a.slug}`}
                    className="block px-4 py-3 hover:bg-field-850/60"
                  >
                    <h3 className="text-sm font-semibold leading-snug">
                      {a.title}
                    </h3>
                    <p className="mt-1 line-clamp-2 text-xs text-chalk-500">
                      {a.excerpt}
                    </p>
                    <p className="mt-1.5 text-[11px] text-chalk-600">
                      {formatDate(a.publishedAt)}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}
